import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useCart } from "@/context/CartContext";
import { http, euro } from "@/lib/api";
import { CouponField } from "./CouponField";

const Field = ({ label, id, ...rest }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs text-[var(--muted)]">{label}</span>
    <input id={id} data-testid={`checkout-${id}-input`} required
      className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base outline-none transition-colors focus:border-[var(--ink)]" {...rest} />
  </label>
);

export const CheckoutDialog = ({ open, onOpenChange }) => {
  const { items, total: cartTotal, subtotal, hasPhysical, clear, setOpen } = useCart();
  const [coupon, setCoupon] = useState(null);
  const total = cartTotal - (coupon?.discount ?? 0);
  const [form, setForm] = useState({ name: "", email: "", address: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => {
    if (!user || !open) return;
    setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user, open]);
  const set = (k) => (e) => {
    if (k === "email") setCoupon(null);
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };
  useEffect(() => {
    setCoupon(null);
  }, [subtotal]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post("/orders", { ...form, coupon_code: coupon?.code ?? null, items: items.map((i) => ({ product_id: i.id, qty: i.qty })) });
      clear();
      onOpenChange(false);
      setOpen(false);
      navigate(`/commande/${data.id}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Merci de vérifier vos informations.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="checkout-modal" className="max-w-lg bg-[var(--bone)]">
        <DialogTitle className="font-display text-3xl font-light">Finaliser la commande</DialogTitle>
        <DialogDescription className="text-sm text-[var(--muted)]">
          Votre commande est enregistrée, puis vous réglez {euro(total)} par carte bancaire ou PayPal, en toute sécurité.
        </DialogDescription>
        <form onSubmit={submit} className="mt-2 space-y-4">
          <Field id="name" label="Nom complet" value={form.name} onChange={set("name")} minLength={2} />
          <Field id="email" label="Email (réception des fichiers & suivi)" type="email" value={form.email} onChange={set("email")} />
          {hasPhysical && <Field id="address" label="Adresse de livraison complète" value={form.address} onChange={set("address")} minLength={8} />}
          <CouponField email={form.email} subtotal={subtotal} coupon={coupon} setCoupon={setCoupon} />
          <p className="text-xs text-[var(--muted)]">En commandant, vous acceptez nos <a href="/legal/cgv" target="_blank" rel="noopener noreferrer" className="underline">CGV</a> et, pour les produits numériques, leur fourniture immédiate.</p>
          <button data-testid="checkout-submit-button" disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--ink)] py-4 text-sm font-medium text-[var(--bone)] transition-colors hover:bg-[var(--terra)] disabled:opacity-60">
            <Lock className="h-4 w-4" /> {loading ? "Enregistrement…" : `Continuer vers le paiement — ${euro(total)}`}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
